import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Link, useLocation } from 'react-router';
import { graphql } from '@/gql';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/cn';
import { formatCents, formatDate, formatInteger } from '@/lib/format';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { Can, hasPermission, usePermissions } from '@/lib/permissions';
import { useTheme } from '@/layout/useTheme';
import { seriesColors } from '../charts/chartPalette';
import { escapeChartText } from '../charts/chartText';
import { thinnedLabelFormatter } from '../charts/ticketSalesAxis';
import { TicketSalesSeriesDoc } from '../charts/ticketSalesSeries';
import { NightsForTicketsFilterDoc } from './adminTickets';
import { buildSalesCurve, lineMarkerSizes, vsText, type SalesCurveView } from './nightSalesCurve';

const NightTicketSummaryDoc = graphql(/* GraphQL */ `
  query NightTicketSummary($nightId: UUID!) {
    nightTicketSummary(nightId: $nightId) {
      sold
      guest
      unclaimed
      left
      revenueCents
      perChannel {
        website
        app
        link
        member
        pos
        door
      }
    }
  }
`);

const NightCompareDoc = graphql(/* GraphQL */ `
  query NightCompare($id: UUID!) {
    night(id: $id) {
      id
      compareNight {
        id
        title
      }
    }
  }
`);

const SetNightCompareNightDoc = graphql(/* GraphQL */ `
  mutation SetNightCompareNight($nightId: UUID!, $compareNightId: UUID) {
    setNightCompareNight(nightId: $nightId, compareNightId: $compareNightId) {
      id
      compareNight {
        id
        title
      }
    }
  }
`);

/** The `/tickets` list of this night, optionally narrowed to the tickets that can still be claimed. */
function ticketsHref(nightId: string, unclaimed: boolean): string {
  const params = new URLSearchParams({ night: nightId });
  if (unclaimed) params.set('unclaimed', '1');
  return `/tickets?${params.toString()}`;
}

/**
 * The sold-per-channel row, one `[id key, label, count]` per channel in display order (Website, App, Link, Member, POS, Door).
 * A channel with no sale is left out, unless nothing sold at all: then every channel shows 0.
 */
function channelCounts(perChannel: {
  website: number;
  app: number;
  link: number;
  member: number;
  pos: number;
  door: number;
}): Array<readonly [string, string, number]> {
  const all: Array<readonly [string, string, number]> = [
    ['website', 'Website', perChannel.website],
    ['app', 'App', perChannel.app],
    ['link', 'Link', perChannel.link],
    ['member', 'Member', perChannel.member],
    ['pos', 'POS', perChannel.pos],
    ['door', 'Door', perChannel.door],
  ];
  const sold = all.filter(([, , count]) => count > 0);
  return sold.length > 0 ? sold : all;
}

const TILE = 'rounded-xl border border-gray-200 p-3 dark:border-white/10';
const TILE_LABEL = 'text-xs font-medium text-gray-500 dark:text-gray-400';
const TILE_VALUE = 'mt-1 text-2xl font-semibold text-gray-900 dark:text-white';

function Tile({ id, label, value, hint }: { id: string; label: string; value: string; hint?: string }) {
  return (
    <div id={id} className={TILE}>
      <div className={TILE_LABEL}>{label}</div>
      <div className={TILE_VALUE}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</div> : null}
    </div>
  );
}

/**
 * The Ticket sales card of a ticketed night (`ticket:read`): tiles, the sales per channel, a cumulative curve of tickets
 * sold against days left with a comparison night, and the links into `/tickets`. Revenue, the curve and Vs need
 * `report:chart:read` and are absent without it. A free night has no card (the page decides).
 */
export function NightSalesCard({ nightId }: { nightId: string }) {
  return (
    <Can permission="ticket:read">
      <SalesCardInner nightId={nightId} />
    </Can>
  );
}

function SalesCardInner({ nightId }: { nightId: string }) {
  const { pathname, search } = useLocation();
  const from = pathname + search;
  const permissions = usePermissions();
  const canChart = hasPermission(permissions, 'report:chart:read');

  const summaryQuery = useQuery(NightTicketSummaryDoc, { variables: { nightId }, errorPolicy: 'all' });
  const summary = (summaryQuery.data ?? summaryQuery.previousData)?.nightTicketSummary;

  return (
    <Card
      id="admin.nights.sales"
      title="Ticket sales"
      subtitle="Sold, guest and unclaimed tickets of this night. Every ticket stays on the tickets list."
      actions={
        <Link
          id="admin.nights.sales.showAll"
          to={ticketsHref(nightId, false)}
          state={{ from }}
          className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          Show all tickets
        </Link>
      }
    >
      <div className="space-y-5">
        {summaryQuery.error && !summary ? (
          <QueryErrorNotice error={summaryQuery.error} title="Could not load the ticket sales" />
        ) : !summary ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <Tile id="admin.nights.sales.sold" label="Sold" value={formatInteger(summary.sold)} />
              <Tile id="admin.nights.sales.guest" label="Guest tickets" value={formatInteger(summary.guest)} />
              {canChart ? (
                <Tile id="admin.nights.sales.revenue" label="Revenue" value={formatCents(summary.revenueCents)} />
              ) : null}
              {summary.left !== null && summary.left !== undefined ? (
                <Tile id="admin.nights.sales.left" label="Left" value={formatInteger(summary.left)} />
              ) : null}
              <Link
                id="admin.nights.sales.unclaimed"
                to={ticketsHref(nightId, true)}
                state={{ from }}
                className={cn(TILE, 'block hover:border-brand-300 dark:hover:border-brand-400/50')}
              >
                <div className={TILE_LABEL}>Still unclaimed</div>
                <div className={TILE_VALUE}>{formatInteger(summary.unclaimed)}</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Show these tickets</div>
              </Link>
              <VsTile nightId={nightId} />
            </div>

            <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300" aria-label="Sold per channel">
              {channelCounts(summary.perChannel).map(([key, label, count]) => (
                <li key={key} id={`admin.nights.sales.channel.${key}`}>
                  <span>{label}</span> <span className="font-semibold text-gray-900 dark:text-white">{formatInteger(count)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {canChart ? <SalesCurve nightId={nightId} /> : null}
      </div>
    </Card>
  );
}

/** The saved comparison night, else the suggestion (Go resolves it), as `id` and `title`; null while loading or none. */
function useCompareNight(nightId: string) {
  const { data, previousData } = useQuery(NightCompareDoc, { variables: { id: nightId }, errorPolicy: 'all' });
  const night = (data ?? previousData)?.night;
  return { loaded: night !== undefined, compare: night?.compareNight ?? null };
}

/** Vs: this night's tickets sold minus the comparison night's at the same days left. Left out when there is no comparison. */
function VsTile({ nightId }: { nightId: string }) {
  const permissions = usePermissions();
  if (!hasPermission(permissions, 'report:chart:read')) return null;
  return <VsTileInner nightId={nightId} />;
}

function VsTileInner({ nightId }: { nightId: string }) {
  const { compare } = useCompareNight(nightId);
  const series = useSalesSeries(nightId);
  const curve = useMemo(
    () =>
      series.payload && compare
        ? buildSalesCurve({
            rows: series.payload.series,
            categories: series.payload.categories,
            nightId,
            compareId: compare.id,
            compareName: compare.title,
            view: 'total',
          })
        : null,
    [series.payload, compare, nightId],
  );
  if (!compare || !curve || curve.vs === null) return null;
  return <Tile id="admin.nights.sales.vs" label={`Vs ${compare.title}`} value={vsText(curve.vs)} />;
}

/** `ticketSalesSeries` for this night and its comparison night, per day; skipped until the comparison is known. */
function useSalesSeries(nightId: string) {
  const { loaded, compare } = useCompareNight(nightId);
  const nightIds = compare ? [nightId, compare.id] : [nightId];
  const query = useQuery(TicketSalesSeriesDoc, {
    skip: !loaded,
    variables: { nightIds, interval: 'DAY' },
    errorPolicy: 'all',
  });
  const payload = (query.data ?? query.previousData)?.ticketSalesSeries;
  return { payload, loading: query.loading && !payload, error: query.error };
}

function SalesCurve({ nightId }: { nightId: string }) {
  const { resolved: theme } = useTheme();
  const permissions = usePermissions();
  const canManage = hasPermission(permissions, 'night:manage');
  const [view, setView] = useState<SalesCurveView>('total');
  const [compareError, setCompareError] = useState<string | null>(null);
  const { compare } = useCompareNight(nightId);
  const series = useSalesSeries(nightId);
  const nightsQuery = useQuery(NightsForTicketsFilterDoc, { errorPolicy: 'all' });
  const [setCompare, { loading: saving }] = useMutation(SetNightCompareNightDoc);

  const options = useMemo(() => {
    const nights = (nightsQuery.data?.nights.nodes ?? []).filter((n) => n.id !== nightId);
    const list = nights.map((n) => ({ value: n.id, label: `${n.title} · ${formatDate(n.startsAt)}` }));
    // The comparison may lie outside the newest 200 nights the list holds: keep it selectable.
    if (compare && !nights.some((n) => n.id === compare.id)) list.unshift({ value: compare.id, label: compare.title });
    // The first option clears the saved choice. The client cannot tell a saved night from the suggested one, so one label is true
    // in both cases: Automatic (Go picks the most recent earlier night of the blueprint, if any).
    return [{ value: '', label: 'Automatic' }, ...list];
  }, [nightsQuery.data, nightId, compare]);

  const curve = useMemo(
    () =>
      series.payload
        ? buildSalesCurve({
            rows: series.payload.series,
            categories: series.payload.categories,
            nightId,
            compareId: compare?.id ?? null,
            compareName: compare?.title ?? null,
            view,
          })
        : null,
    [series.payload, nightId, compare, view],
  );

  // Short labels (the number of days; 0 is the night itself) so a long span stays readable; the axis title says what they are.
  const categories = useMemo(() => (series.payload?.categories ?? []).map(String), [series.payload]);
  const rawNames = curve?.lines.map((l) => l.name) ?? [];
  // For the test hooks: the last value each line draws (this night's stops at today, the comparison runs on to doors close).
  const lastValues = (curve?.lines ?? []).map((l) => ({ name: l.name, last: l.data.filter((v) => v !== null).at(-1) ?? null }));
  const apexSeries = useMemo(() => {
    const seen = new Set<string>();
    return (curve?.lines ?? []).map((l) => {
      // Apex writes names with innerHTML (escape them); two equal names get an invisible word joiner.
      let name = escapeChartText(l.name);
      while (seen.has(name)) name += '⁠';
      seen.add(name);
      return { name, data: l.data };
    });
  }, [curve]);

  const dark = theme === 'dark';
  // This night's lines wear the shared categorical colours in order; the comparison is a neutral grey (and dashed), so it reads
  // as the yardstick on a light and a dark surface alike.
  const colors = useMemo(() => {
    const mine = seriesColors((curve?.lines ?? []).filter((l) => !l.comparison).length);
    let next = 0;
    return (curve?.lines ?? []).map((l) => (l.comparison ? (dark ? '#94a3b8' : '#64748b') : (mine[next++] ?? '#fc0090')));
  }, [curve, dark]);
  const chartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'line',
        toolbar: { show: false },
        fontFamily: 'inherit',
        animations: { enabled: false },
        height: 320,
        foreColor: dark ? '#d1d5db' : '#4b5563',
        background: 'transparent',
      },
      colors,
      // The comparison is dashed so it is not told apart by colour alone.
      stroke: { width: 2, curve: 'straight', dashArray: apexSeries.map((_, i) => (curve?.lines[i]?.comparison ? 6 : 0)) },
      markers: { size: lineMarkerSizes(curve?.lines ?? []), hover: { size: 5 } },
      xaxis: {
        categories,
        tickPlacement: 'between',
        labels: { hideOverlappingLabels: false, trim: false, formatter: thinnedLabelFormatter(categories) },
        title: { text: 'Days before doors close', style: { fontWeight: 400 } },
        axisBorder: { color: dark ? '#374151' : '#e5e7eb' },
        axisTicks: { color: dark ? '#374151' : '#e5e7eb' },
      },
      yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v: number) => String(Math.round(v)) } },
      grid: { borderColor: dark ? '#1f2937' : '#f3f4f6' },
      legend: { position: 'top', showForSingleSeries: true },
      dataLabels: { enabled: false },
      tooltip: {
        theme: dark ? 'dark' : 'light',
        shared: true,
        intersect: false,
        y: { formatter: (v: number | null) => (v === null ? '' : `${v} ${v === 1 ? 'ticket' : 'tickets'}`) },
      },
    }),
    [categories, colors, apexSeries, curve, dark],
  );
  const chartKey = [view, dark, categories.length, categories[0], rawNames.join('|'), (series.payload?.series ?? []).map((s) => s.pending).join(',')].join('::');

  const hasData = (series.payload?.categories.length ?? 0) > 0 && (curve?.lines.length ?? 0) > 0;

  return (
    <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-white/10"
          role="group"
          aria-label="Curve view"
        >
          {(
            [
              ['total', 'Total'],
              ['stage', 'Per stage'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              id={`admin.nights.sales.view.${v}`}
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium',
                view === v
                  ? 'bg-brand-500 text-white'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Compare with</span>
          <Select
            id="admin.nights.sales.compare"
            className="w-80"
            aria-label="Comparison night"
            value={compare?.id ?? ''}
            disabled={!canManage || saving}
            options={options}
            onChange={async (e) => {
              setCompareError(null);
              try {
                await setCompare({ variables: { nightId, compareNightId: e.target.value || null } });
              } catch (err) {
                setCompareError(graphqlErrorMessage(err, 'Could not save the comparison night.'));
              }
            }}
          />
        </div>
      </div>
      <p id="admin.nights.sales.compareHint" className="text-xs text-gray-500 dark:text-gray-400">
        Automatic compares with the most recent earlier night of the same blueprint, when there is one.
      </p>
      {compareError ? (
        <p id="admin.nights.sales.compareError" className="text-sm text-red-600 dark:text-red-400">
          {compareError}
        </p>
      ) : null}

      {series.error && !series.payload ? (
        <QueryErrorNotice error={series.error} title="Could not load the sales curve" />
      ) : series.loading ? (
        <p className="py-10 text-center text-sm text-gray-500">Loading…</p>
      ) : !hasData ? (
        <EmptyState title="No ticket sales yet" description="The curve appears with the first sold ticket." />
      ) : (
        <div id="admin.nights.sales.chart" data-series={JSON.stringify(rawNames)} data-lines={JSON.stringify(lastValues)}>
          <ReactApexChart key={chartKey} options={chartOptions} series={apexSeries} type="line" height={320} />
        </div>
      )}
    </div>
  );
}
